/**
 * GraphLegend Component - Visual Legend for Graph Node Types
 * Baseado em best practices 2025: Color coding, shape differentiation, interactive
 *
 * Padrão:
 * - Color + Shape para acessibilidade (colorblind-friendly)
 * - Interactive hover effects (dim outros tipos)
 * - Click to isolate (opcional)
 * - Marker com border-radius
 *
 * Referências:
 * - https://carbondesignsystem.com/data-visualization/legends/
 * - https://mui.com/x/react-charts/legend/
 * - https://flourish.studio/blog/legend-colors-in-header/
 */

import { useState } from 'react';
import clsx from 'clsx';
import type { TipoNo } from '../../types/investigation';

interface LegendItem {
  type: TipoNo;
  label: string;
  color: string;
  shape: 'circle' | 'square' | 'triangle' | 'diamond';
  description?: string;
}

const LEGEND_ITEMS: LegendItem[] = [
  {
    type: 'cnpj',
    label: 'CNPJ',
    color: '#3B82F6', // blue-500
    shape: 'square',
    description: 'Empresas e entidades jurídicas',
  },
  {
    type: 'pessoa',
    label: 'Pessoa',
    color: '#10B981', // green-500
    shape: 'circle',
    description: 'Pessoas físicas (sócios)',
  },
  {
    type: 'email',
    label: 'Email',
    color: '#F59E0B', // amber-500
    shape: 'triangle',
    description: 'Endereços de email',
  },
  {
    type: 'telefone',
    label: 'Telefone',
    color: '#8B5CF6', // violet-500
    shape: 'triangle',
    description: 'Números de telefone',
  },
  {
    type: 'cpf-nome',
    label: 'CPF+Nome',
    color: '#EC4899', // pink-500
    shape: 'diamond',
    description: 'Busca por CPF e nome',
  },
];

interface GraphLegendProps {
  onTypeFilter?: (types: TipoNo[]) => void;
  interactive?: boolean;
  compact?: boolean;
  className?: string;
}

/**
 * Legenda visual do grafo
 *
 * Features:
 * - ✅ Cores e formas para cada tipo de nó
 * - ✅ Hover effects (opcional)
 * - ✅ Click to filter (opcional)
 * - ✅ Modo compacto (mobile)
 * - ✅ Acessível (screen reader friendly)
 *
 * @example
 * <GraphLegend
 *   onTypeFilter={(types) => filterGraph(types)}
 *   interactive
 * />
 */
export function GraphLegend({
  onTypeFilter,
  interactive = false,
  compact = false,
  className = '',
}: GraphLegendProps) {
  const [selectedTypes, setSelectedTypes] = useState<TipoNo[]>([]);
  const [hoveredType, setHoveredType] = useState<TipoNo | null>(null);

  const handleClick = (type: TipoNo) => {
    if (!interactive || !onTypeFilter) return;

    const newSelectedTypes = selectedTypes.includes(type)
      ? selectedTypes.filter((t) => t !== type)
      : [...selectedTypes, type];

    setSelectedTypes(newSelectedTypes);
    onTypeFilter(newSelectedTypes);
  };

  return (
    <div
      className={clsx(
        'bg-white border border-gray-200 rounded-lg',
        compact ? 'p-3' : 'p-4',
        className
      )}
      role="region"
      aria-label="Legenda do grafo"
    >
      {/* Title */}
      {!compact && (
        <h3 className="text-sm font-semibold text-gray-900 mb-3">
          Tipos de Nós
        </h3>
      )}

      {/* Legend items - Grid responsivo */}
      <div
        className={clsx(
          'grid gap-2',
          compact
            ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5'
            : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-1'
        )}
      >
        {LEGEND_ITEMS.map((item) => {
          const isSelected = selectedTypes.length === 0 || selectedTypes.includes(item.type);
          const isHovered = hoveredType === item.type;
          const isDimmed = hoveredType !== null && !isHovered;

          return (
            <button
              key={item.type}
              type="button"
              onClick={() => handleClick(item.type)}
              onMouseEnter={() => interactive && setHoveredType(item.type)}
              onMouseLeave={() => interactive && setHoveredType(null)}
              disabled={!interactive}
              className={clsx(
                'flex items-center space-x-2 rounded-md transition-all duration-200 min-h-[44px]',
                interactive && 'hover:bg-gray-50 cursor-pointer p-2',
                !interactive && 'cursor-default',
                isDimmed && 'opacity-30',
                !isSelected && interactive && 'opacity-50'
              )}
              aria-label={`${item.label}: ${item.description}`}
              title={item.description}
            >
              {/* Shape Marker */}
              <NodeShapeMarker
                shape={item.shape}
                color={item.color}
                size={compact ? 'sm' : 'md'}
              />

              {/* Label */}
              <div className="flex-1 text-left min-w-0">
                <span className={clsx(
                  'font-medium',
                  compact ? 'text-xs' : 'text-sm',
                  isSelected ? 'text-gray-900' : 'text-gray-500'
                )}>
                  {item.label}
                </span>
                {!compact && item.description && (
                  <p className="text-xs text-gray-500 truncate">
                    {item.description}
                  </p>
                )}
              </div>

              {/* Checkmark se selecionado (modo interativo) */}
              {interactive && isSelected && selectedTypes.length > 0 && (
                <svg
                  className="h-4 w-4 text-blue-600 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
          );
        })}
      </div>

      {/* Reset filter button (se tiver filtros ativos) */}
      {interactive && selectedTypes.length > 0 && (
        <button
          onClick={() => {
            setSelectedTypes([]);
            onTypeFilter?.([]);
          }}
          className="mt-3 w-full text-xs text-blue-600 hover:text-blue-700 font-medium py-2 rounded hover:bg-blue-50 transition-colors"
        >
          Limpar Filtros
        </button>
      )}
    </div>
  );
}

/**
 * Node Shape Marker - Renderiza forma geométrica do tipo de nó
 */
interface NodeShapeMarkerProps {
  shape: 'circle' | 'square' | 'triangle' | 'diamond';
  color: string;
  size?: 'sm' | 'md' | 'lg';
}

function NodeShapeMarker({ shape, color, size = 'md' }: NodeShapeMarkerProps) {
  const sizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-6 h-6',
  };

  const baseClasses = `flex-shrink-0 ${sizeClasses[size]}`;

  switch (shape) {
    case 'circle':
      return (
        <div
          className={`${baseClasses} rounded-full`}
          style={{ backgroundColor: color }}
          aria-hidden="true"
        />
      );

    case 'square':
      return (
        <div
          className={`${baseClasses} rounded-sm`}
          style={{ backgroundColor: color }}
          aria-hidden="true"
        />
      );

    case 'triangle':
      return (
        <div
          className={`${baseClasses} relative`}
          aria-hidden="true"
        >
          <svg viewBox="0 0 100 100" className="w-full h-full">
            <polygon
              points="50,15 90,85 10,85"
              fill={color}
            />
          </svg>
        </div>
      );

    case 'diamond':
      return (
        <div
          className={`${baseClasses} rotate-45`}
          style={{ backgroundColor: color }}
          aria-hidden="true"
        />
      );

    default:
      return null;
  }
}
